import {
  Add01Icon,
  AiChat02Icon,
  AlertCircleIcon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  BookOpen01Icon,
  Brain01Icon,
  Calendar03Icon,
  Cancel01Icon,
  DashboardSpeed01Icon,
  DashboardSquare01Icon,
  Download01Icon,
  FilterHorizontalIcon,
  FireIcon,
  FocusIcon,
  HelpCircleIcon,
  Logout01Icon,
  MoreHorizontalCircle01Icon,
  NotebookPenIcon,
  PartyPopperIcon,
  RankingIcon,
  RotateCcwIcon,
  Search01Icon,
  Settings02Icon,
  ShieldUserIcon,
  SmartphoneIcon,
  SparklesIcon,
  Tick02Icon,
  TimerResetIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { addDays, formatLongDate, formatShortDate, todayInTimezone } from '../domain/date';
import {
  addExtraWork,
  addManualSession,
  applyEndDay,
  markUnavailableDay,
  moveItemAhead,
  orderedItems,
  previewEndDay,
  previewUnavailableDay,
  projectedFinishDate,
  restoreAvailableDay,
  setDppAccuracy,
  togglePart,
  updateSuggestion,
} from '../domain/schedule';
import type {
  Mistake,
  RescheduleProposal,
  ScheduleItem,
  TaskPart,
  TrackerDocument,
  TrackerEnvelope,
  UnavailableReason,
} from '../domain/types';
import type { SubjectPlanSummary } from '../domain/platform';
import {
  loadPlan,
  loadPlans,
  loadSession,
  loadTracker,
  logout,
  savePlan,
  saveTracker,
  updatePlanSettings,
  type SessionUser,
} from './api';
import { Onboarding } from './Onboarding';
import { AdminView, AssistantView, LeaderboardView, SettingsView } from './ProductPanels';
import { ActionToast, ConnectionBadge, MeshBackdrop, ShortcutDialog } from './qol';
import { applyAccent, DEFAULT_ACCENT } from './theme';

type Tab =
  | 'today'
  | 'schedule'
  | 'assistant'
  | 'progress'
  | 'mistakes'
  | 'leaderboard'
  | 'settings'
  | 'admin'
  | 'more';
type SaveStatus = 'saved' | 'saving' | 'error';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const partLabels: Record<TaskPart, string> = {
  lecture: 'Lecture watched',
  notes: 'Notes made',
  dpp: 'DPP done',
  review: 'Quick review done',
};

export function App() {
  const [boot, setBoot] = useState<'loading' | 'ready' | 'error'>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [envelope, setEnvelope] = useState<TrackerEnvelope | null>(null);
  const [plans, setPlans] = useState<SubjectPlanSummary[]>([]);
  const [activePlanId, setActivePlanId] = useState<string | null>(null);
  const [activeAutoRebalance, setActiveAutoRebalance] = useState(true);
  const [tab, setTab] = useState<Tab>('today');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [notice, setNotice] = useState<{ message: string; canUndo: boolean } | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const versionRef = useRef(0);
  const saveQueue = useRef(Promise.resolve());
  const bootStarted = useRef(false);
  const envelopeRef = useRef<TrackerEnvelope | null>(null);
  const undoDocumentRef = useRef<TrackerDocument | null>(null);
  const pendingDocumentRef = useRef<TrackerDocument | null>(null);
  const wasOnlineRef = useRef(navigator.onLine);
  const activePlanIdRef = useRef<string | null>(null);
  const legalPage =
    window.location.pathname === '/privacy'
      ? 'privacy'
      : window.location.pathname === '/terms'
        ? 'terms'
        : null;
  const adminPage = window.location.pathname === '/admin';
  const publicLeaderboardPage = window.location.pathname === '/leaderboard';

  useEffect(() => {
    const pathname = window.location.pathname;
    const metadata =
      pathname === '/leaderboard'
        ? {
            title: 'Study consistency leaderboard | tarpy.party',
            description:
              'See the public tarpy.party study consistency leaderboard, built from real completed sessions and streaks.',
            canonical: 'https://tarpy.party/leaderboard',
          }
        : pathname === '/privacy'
          ? {
              title: 'Privacy | tarpy.party',
              description: 'How tarpy.party handles study planning and account data.',
              canonical: 'https://tarpy.party/privacy',
            }
          : pathname === '/terms'
            ? {
                title: 'Terms | tarpy.party',
                description: 'Terms for using the tarpy.party adaptive study planner.',
                canonical: 'https://tarpy.party/terms',
              }
            : {
                title: 'tarpy.party — adaptive study tracking',
                description:
                  'Build an adaptive subject-wise study calendar for Class 9–12, track partial progress and DPP accuracy, and automatically reschedule missed work.',
                canonical: 'https://tarpy.party/',
              };

    window.document.title = metadata.title;
    window.document
      .querySelector<HTMLMetaElement>('#page-description')
      ?.setAttribute('content', metadata.description);
    window.document
      .querySelector<HTMLLinkElement>('#canonical-url')
      ?.setAttribute('href', metadata.canonical);
    window.document
      .querySelector<HTMLMetaElement>('#og-title')
      ?.setAttribute('content', metadata.title);
    window.document
      .querySelector<HTMLMetaElement>('#og-description')
      ?.setAttribute('content', metadata.description);
    window.document
      .querySelector<HTMLMetaElement>('#og-url')
      ?.setAttribute('content', metadata.canonical);
    window.document
      .querySelector<HTMLMetaElement>('#twitter-title')
      ?.setAttribute('content', metadata.title);
    window.document
      .querySelector<HTMLMetaElement>('#twitter-description')
      ?.setAttribute('content', metadata.description);
  }, []);

  useEffect(() => {
    if (!user) applyAccent(DEFAULT_ACCENT);
    else if (user.onboardingCompleted) applyAccent(user.accentColor);
    // During onboarding, the draft owns the live accent until it is persisted with the profile.
  }, [user]);

  useEffect(() => {
    if (bootStarted.current) return;
    bootStarted.current = true;
    void (async () => {
      try {
        const session = await loadSession();
        setUser(session);
        if (session?.onboardingCompleted) {
          const subjectPlans = await loadPlans();
          setPlans(subjectPlans);
          const firstPlan = subjectPlans[0];
          const tracker = firstPlan ? await loadPlan(firstPlan.id) : await loadTracker();
          setActiveAutoRebalance(firstPlan?.autoRebalance ?? true);
          activePlanIdRef.current = firstPlan?.id ?? null;
          setActivePlanId(firstPlan?.id ?? null);
          versionRef.current = tracker.version;
          envelopeRef.current = tracker;
          setEnvelope(tracker);
        }
        setBoot('ready');
      } catch {
        setBoot('error');
      }
    })();
  }, []);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    const captureInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('beforeinstallprompt', captureInstall);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('beforeinstallprompt', captureInstall);
    };
  }, []);

  const commit = useCallback((document: TrackerDocument, undoable = true) => {
    const previous = envelopeRef.current?.document ?? null;
    if (undoable && previous) {
      undoDocumentRef.current = previous;
      setNotice({ message: 'Change queued', canUndo: true });
    }
    pendingDocumentRef.current = document;
    envelopeRef.current = envelopeRef.current ? { ...envelopeRef.current, document } : null;
    setEnvelope((current) => (current ? { ...current, document } : current));
    if (!navigator.onLine) {
      setSaveStatus('error');
      setSaveError('Offline — reconnect to save this change.');
      return;
    }
    setSaveStatus('saving');
    setSaveError(null);
    saveQueue.current = saveQueue.current.then(async () => {
      try {
        const result = activePlanIdRef.current
          ? await savePlan(activePlanIdRef.current, versionRef.current, document)
          : await saveTracker(versionRef.current, document);
        versionRef.current = result.version;
        setEnvelope((current) => {
          const next = current
            ? { ...current, version: result.version, updatedAt: result.updatedAt }
            : current;
          envelopeRef.current = next;
          return next;
        });
        if (pendingDocumentRef.current === document) pendingDocumentRef.current = null;
        setSaveStatus('saved');
      } catch (error) {
        setSaveStatus('error');
        setSaveError(error instanceof Error ? error.message : 'Could not save this change.');
      }
    });
  }, []);

  const undoLast = useCallback(() => {
    const previous = undoDocumentRef.current;
    if (!previous) return;
    undoDocumentRef.current = null;
    setNotice({ message: 'Last change undone', canUndo: false });
    commit(previous, false);
  }, [commit]);

  useEffect(() => {
    const reconnected = online && !wasOnlineRef.current;
    wasOnlineRef.current = online;
    if (reconnected && pendingDocumentRef.current) commit(pendingDocumentRef.current, false);
  }, [commit, online]);

  useEffect(() => {
    const shortcuts: Record<string, Tab> = {
      '1': 'today',
      '2': 'schedule',
      '3': 'progress',
      '4': 'mistakes',
    };
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping = target?.matches('input, textarea, select, [contenteditable="true"]');
      if (event.key === 'Escape') setShortcutsOpen(false);
      if (isTyping) return;
      if (event.altKey && shortcuts[event.key]) {
        event.preventDefault();
        setTab(shortcuts[event.key]!);
      } else if (event.key === '/') {
        event.preventDefault();
        setTab('schedule');
        window.setTimeout(() => window.document.getElementById('schedule-search')?.focus(), 0);
      } else if (event.key === '?') {
        setShortcutsOpen(true);
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        undoLast();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [undoLast]);

  async function installApp() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }

  async function signOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
      window.location.assign('/');
    } catch (error) {
      setNotice({
        message: error instanceof Error ? error.message : 'Could not log out. Please try again.',
        canUndo: false,
      });
      setSigningOut(false);
    }
  }

  if (legalPage) return <LegalPage page={legalPage} />;

  if (boot === 'loading') return <BootScreen />;
  if (boot === 'error') return <ErrorScreen />;
  if (publicLeaderboardPage) return <PublicLeaderboardPage signedIn={Boolean(user)} />;
  if (!user) return <Landing />;
  if (adminPage)
    return user.isAdmin ? (
      <AdminPortal user={user} signingOut={signingOut} onSignOut={signOut} />
    ) : (
      <AdminAccessDenied />
    );
  if (!user.onboardingCompleted)
    return <Onboarding userId={user.id} initialName={user.displayName.split(' ')[0] ?? ''} />;
  if (!envelope) return <BootScreen />;

  const document = envelope.document;
  const today = todayInTimezone(document.timezone);

  async function switchPlan(planId: string) {
    if (planId === activePlanId) return;
    setSaveStatus('saving');
    try {
      const next = await loadPlan(planId);
      activePlanIdRef.current = planId;
      setActivePlanId(planId);
      setActiveAutoRebalance(next.autoRebalance);
      versionRef.current = next.version;
      envelopeRef.current = next;
      setEnvelope(next);
      undoDocumentRef.current = null;
      pendingDocumentRef.current = null;
      setSaveStatus('saved');
      setTab('today');
    } catch (error) {
      setSaveStatus('error');
      setSaveError(error instanceof Error ? error.message : 'Could not open this subject.');
    }
  }

  async function setAutoRebalance(enabled: boolean) {
    if (!activePlanId) return;
    await setPlanAutoRebalance(activePlanId, enabled);
  }

  async function setPlanAutoRebalance(planId: string, enabled: boolean) {
    const target = plans.find((plan) => plan.id === planId);
    const previous = target?.autoRebalance ?? true;
    if (planId === activePlanId) setActiveAutoRebalance(enabled);
    setPlans((current) =>
      current.map((plan) => (plan.id === planId ? { ...plan, autoRebalance: enabled } : plan)),
    );
    try {
      await updatePlanSettings(planId, enabled);
      setNotice({ message: enabled ? 'Auto-rebalance on' : 'Auto-rebalance off', canUndo: false });
    } catch (error) {
      if (planId === activePlanId) setActiveAutoRebalance(previous);
      setPlans((current) =>
        current.map((plan) => (plan.id === planId ? { ...plan, autoRebalance: previous } : plan)),
      );
      setSaveError(error instanceof Error ? error.message : 'Could not update this setting.');
      throw error;
    }
  }

  return (
    <div className="app-shell">
      <MeshBackdrop quiet />
      <header className="topbar">
        <a className="brand-lockup" href="/" aria-label="tarpy.party home">
          <img src="/brand/icon.svg" alt="" className="brand-mark" />
          <span>
            tarpy<span>.party</span>
          </span>
        </a>
        <div className="topbar-actions">
          <ConnectionBadge online={online} />
          <SaveIndicator status={saveStatus} error={saveError} />
          {installPrompt && (
            <button className="install-button" type="button" onClick={() => void installApp()}>
              <HugeiconsIcon icon={SmartphoneIcon} size={14} /> Install
            </button>
          )}
          <button
            className="icon-button shortcut-button"
            type="button"
            onClick={() => setShortcutsOpen(true)}
            aria-label="Keyboard shortcuts"
          >
            <HugeiconsIcon icon={HelpCircleIcon} size={17} />
          </button>
          <div className="profile-chip">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" referrerPolicy="no-referrer" />
            ) : (
              <span>{user.displayName.at(0)}</span>
            )}
            <strong>{user.displayName.split(' ')[0]}</strong>
          </div>
          <button
            className="logout-button"
            type="button"
            disabled={signingOut}
            onClick={() => void signOut()}
          >
            <HugeiconsIcon icon={Logout01Icon} size={17} />
            <span>{signingOut ? 'Logging out…' : 'Log out'}</span>
          </button>
        </div>
      </header>

      <main className="workspace">
        <aside className="sidebar" aria-label="Main navigation">
          <div className="plan-identity">
            <span className="eyebrow">Subject</span>
            {plans.length > 1 ? (
              <select
                className="subject-switcher"
                aria-label="Current subject"
                value={activePlanId ?? ''}
                onChange={(event) => void switchPlan(event.target.value)}
              >
                {plans.map((plan) => (
                  <option value={plan.id} key={plan.id}>
                    {plan.title}
                  </option>
                ))}
              </select>
            ) : (
              <h1>{document.title}</h1>
            )}
            <p>{document.subject}</p>
          </div>
          <nav>
            <NavButton
              active={tab === 'today'}
              icon={<HugeiconsIcon icon={DashboardSquare01Icon} />}
              label="Today"
              onClick={() => setTab('today')}
            />
            <NavButton
              active={tab === 'schedule'}
              icon={<HugeiconsIcon icon={Calendar03Icon} />}
              label="Schedule"
              onClick={() => setTab('schedule')}
            />
            <NavButton
              active={tab === 'assistant'}
              icon={<HugeiconsIcon icon={AiChat02Icon} />}
              label="Assistant"
              onClick={() => setTab('assistant')}
            />
            <NavButton
              active={tab === 'progress'}
              icon={<HugeiconsIcon icon={DashboardSpeed01Icon} />}
              label="Progress"
              onClick={() => setTab('progress')}
            />
            <NavButton
              active={tab === 'mistakes'}
              icon={<HugeiconsIcon icon={NotebookPenIcon} />}
              label="Mistakes"
              onClick={() => setTab('mistakes')}
            />
            <NavButton
              active={tab === 'leaderboard'}
              icon={<HugeiconsIcon icon={RankingIcon} />}
              label="Leaderboard"
              onClick={() => setTab('leaderboard')}
            />
            <NavButton
              active={tab === 'settings'}
              icon={<HugeiconsIcon icon={Settings02Icon} />}
              label="Settings"
              onClick={() => setTab('settings')}
            />
            {user.isAdmin && (
              <NavButton
                active={tab === 'admin'}
                icon={<HugeiconsIcon icon={ShieldUserIcon} />}
                label="Admin"
                onClick={() => setTab('admin')}
              />
            )}
          </nav>
          <PaceCard document={document} today={today} />
        </aside>

        <section
          className="content subject-enter"
          key={activePlanId ?? 'legacy'}
          aria-live="polite"
        >
          {plans.length > 1 && (
            <label className="mobile-subject-select">
              <span>Subject</span>
              <div>
                <select
                  aria-label="Current subject"
                  value={activePlanId ?? ''}
                  onChange={(event) => void switchPlan(event.target.value)}
                >
                  {plans.map((plan) => (
                    <option value={plan.id} key={plan.id}>
                      {plan.title}
                    </option>
                  ))}
                </select>
                <small>{document.subject}</small>
              </div>
            </label>
          )}
          {tab === 'today' && (
            <TodayView
              document={document}
              today={today}
              commit={commit}
              autoRebalance={activeAutoRebalance}
            />
          )}
          {tab === 'schedule' && (
            <ScheduleView
              document={document}
              today={today}
              commit={commit}
              autoRebalance={activeAutoRebalance}
              onAutoRebalanceChange={(enabled) => void setAutoRebalance(enabled)}
            />
          )}
          {tab === 'assistant' && activePlanId && (
            <AssistantView planId={activePlanId} subject={document.title} />
          )}
          {tab === 'progress' && <ProgressView document={document} today={today} />}
          {tab === 'mistakes' && <MistakesView document={document} commit={commit} />}
          {tab === 'leaderboard' && <LeaderboardView currentUserId={user.id} />}
          {tab === 'settings' && (
            <SettingsView
              user={user}
              plans={plans}
              onSaved={(profile) =>
                setUser((current) =>
                  current
                    ? {
                        ...current,
                        username: profile.username,
                        grade: profile.grade,
                        curriculum: profile.curriculum,
                        examTrack: profile.examTrack,
                        leaderboardVisible: profile.leaderboardVisible,
                        timezone: profile.timezone,
                        accentColor: profile.accentColor,
                      }
                    : current,
                )
              }
              onAutoRebalanceChange={setPlanAutoRebalance}
              onSignOut={signOut}
              signingOut={signingOut}
            />
          )}
          {tab === 'admin' && user.isAdmin && <AdminView />}
          {tab === 'more' && (
            <MoreView
              isAdmin={user.isAdmin}
              onSelect={(nextTab) => setTab(nextTab)}
              onSignOut={signOut}
              signingOut={signingOut}
            />
          )}
        </section>
      </main>

      <nav className="mobile-nav" aria-label="Main navigation">
        <NavButton
          active={tab === 'today'}
          icon={<HugeiconsIcon icon={DashboardSquare01Icon} />}
          label="Today"
          onClick={() => setTab('today')}
        />
        <NavButton
          active={tab === 'schedule'}
          icon={<HugeiconsIcon icon={Calendar03Icon} />}
          label="Plan"
          onClick={() => setTab('schedule')}
        />
        <NavButton
          active={tab === 'assistant'}
          icon={<HugeiconsIcon icon={AiChat02Icon} />}
          label="AI"
          onClick={() => setTab('assistant')}
        />
        <NavButton
          active={tab === 'progress'}
          icon={<HugeiconsIcon icon={DashboardSpeed01Icon} />}
          label="Progress"
          onClick={() => setTab('progress')}
        />
        <NavButton
          active={['more', 'mistakes', 'leaderboard', 'settings', 'admin'].includes(tab)}
          icon={<HugeiconsIcon icon={MoreHorizontalCircle01Icon} />}
          label="More"
          onClick={() => setTab('more')}
        />
      </nav>
      {notice && (
        <ActionToast
          message={notice.message}
          canUndo={notice.canUndo}
          onUndo={undoLast}
          onClose={() => setNotice(null)}
        />
      )}
      {shortcutsOpen && <ShortcutDialog onClose={() => setShortcutsOpen(false)} />}
    </div>
  );
}

function TodayView({ document, today, commit, autoRebalance = false }: ViewProps) {
  const [proposal, setProposal] = useState<RescheduleProposal | null>(null);
  const [focusMode, setFocusMode] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false);
  const [extraTopic, setExtraTopic] = useState('');
  const [extraMinutes, setExtraMinutes] = useState(30);
  const items = orderedItems(document).filter(
    (item) =>
      item.date === today && item.suggestionStatus !== 'pending' && item.status !== 'skipped',
  );
  const pending = orderedItems(document).filter((item) => item.suggestionStatus === 'pending');
  const primary = items[0];

  return (
    <>
      <PageHeader
        eyebrow={formatLongDate(today)}
        title="Today"
        action={
          <div className="page-actions">
            <button
              className="secondary-button"
              type="button"
              onClick={() => setExtraOpen((value) => !value)}
            >
              <HugeiconsIcon icon={Add01Icon} size={15} /> Extra
            </button>
            {primary && (
              <button
                className={`secondary-button ${focusMode ? 'active-control' : ''}`}
                type="button"
                onClick={() => setFocusMode((current) => !current)}
              >
                <HugeiconsIcon icon={FocusIcon} size={15} /> {focusMode ? 'Show all' : 'Focus'}
              </button>
            )}
          </div>
        }
      />
      <AdaptiveBrief document={document} today={today} items={items} />
      {extraOpen && (
        <form
          className="extra-work card"
          onSubmit={(event) => {
            event.preventDefault();
            if (!extraTopic.trim()) return;
            commit(
              addExtraWork(document, {
                topic: extraTopic,
                minutes: extraMinutes,
                date: today,
              }),
            );
            setExtraTopic('');
            setExtraOpen(false);
          }}
        >
          <input
            value={extraTopic}
            onChange={(event) => setExtraTopic(event.target.value)}
            placeholder="Extra topic completed"
            autoFocus
          />
          <input
            aria-label="Extra minutes"
            type="number"
            min="5"
            max="600"
            step="5"
            value={extraMinutes}
            onChange={(event) => setExtraMinutes(Number(event.target.value))}
          />
          <button className="small-button" type="submit">
            Log
          </button>
        </form>
      )}
      {primary ? (
        <TaskCard
          item={primary}
          onToggle={(part) => commit(togglePart(document, primary.id, part, today))}
          onAccuracy={(accuracy) => commit(setDppAccuracy(document, primary.id, accuracy))}
          onEndDay={() => setProposal(previewEndDay(document, primary.id, today, autoRebalance))}
        />
      ) : (
        <EmptyToday
          next={orderedItems(document).find(
            (item) => item.date > today && item.status === 'scheduled',
          )}
        />
      )}
      {!focusMode &&
        items
          .slice(1)
          .map((item) => (
            <TaskCard
              key={item.id}
              item={item}
              onToggle={(part) => commit(togglePart(document, item.id, part, today))}
              onAccuracy={(accuracy) => commit(setDppAccuracy(document, item.id, accuracy))}
              onEndDay={() => setProposal(previewEndDay(document, item.id, today, autoRebalance))}
            />
          ))}
      {!focusMode && pending.length > 0 && (
        <SuggestionList document={document} items={pending} commit={commit} />
      )}
      {proposal && (
        <RescheduleDialog
          proposal={proposal}
          onClose={() => setProposal(null)}
          onApply={(acceptedProposal) => {
            commit(applyEndDay(document, acceptedProposal, today));
            setProposal(null);
          }}
        />
      )}
    </>
  );
}

function AdaptiveBrief({
  document,
  today,
  items,
}: {
  document: TrackerDocument;
  today: string;
  items: ScheduleItem[];
}) {
  const incompleteParts = items.flatMap((item) =>
    (Object.keys(item.parts) as TaskPart[])
      .filter((part) => !item.parts[part])
      .map((part) => ({ item, part })),
  );
  const remainingMinutes = items.reduce((total, item) => {
    const parts = Object.values(item.parts);
    const incomplete = parts.filter((done) => !done).length;
    return total + Math.round(item.durationMinutes * (incomplete / Math.max(parts.length, 1)));
  }, 0);
  const pendingReviews = document.items.filter(
    (item) => item.suggestionStatus === 'pending' && item.kind === 'revision',
  ).length;
  const next = incompleteParts[0];
  const load = remainingMinutes <= 45 ? 'Light' : remainingMinutes <= 100 ? 'Steady' : 'Full';

  return (
    <section className="adaptive-brief" aria-label="Adaptive daily brief">
      <span>
        <HugeiconsIcon icon={SparklesIcon} size={14} />
        <b>{load} load</b>
        <small>{remainingMinutes} min left</small>
      </span>
      <span>
        <HugeiconsIcon icon={FocusIcon} size={14} />
        <b>{next ? partLabels[next.part] : 'Plan clear'}</b>
        <small>{next?.item.topic ?? formatShortDate(today)}</small>
      </span>
      <span>
        <HugeiconsIcon icon={Brain01Icon} size={14} />
        <b>
          {pendingReviews || 'No'} review{pendingReviews === 1 ? '' : 's'}
        </b>
        <small>accuracy-led</small>
      </span>
    </section>
  );
}

interface ViewProps {
  document: TrackerDocument;
  today: string;
  commit: (document: TrackerDocument) => void;
  autoRebalance?: boolean;
  onAutoRebalanceChange?: (enabled: boolean) => void;
}

function TaskCard({
  item,
  onToggle,
  onAccuracy,
  onEndDay,
}: {
  item: ScheduleItem;
  onToggle: (part: TaskPart) => void;
  onAccuracy: (value: number) => void;
  onEndDay: () => void;
}) {
  const completed = Object.values(item.parts).filter(Boolean).length;
  const total = Object.keys(item.parts).length;
  return (
    <article className="task-card card">
      <div className="task-heading">
        <div>
          <span className={`kind-badge kind-${item.kind}`}>{kindLabel(item.kind)}</span>
          <h2>{item.topic}</h2>
          <p>
            {item.chapter} · about {item.durationMinutes} min
          </p>
        </div>
        <div
          className="ring"
          style={{ '--progress': `${(completed / total) * 360}deg` } as React.CSSProperties}
        >
          <span>
            {completed}/{total}
          </span>
        </div>
      </div>

      <div className="part-list">
        {(Object.keys(item.parts) as TaskPart[]).map((part) => (
          <button
            className={`part-toggle ${item.parts[part] ? 'checked' : ''}`}
            type="button"
            key={part}
            onClick={() => onToggle(part)}
          >
            <span className="check-box">
              {item.parts[part] && <HugeiconsIcon icon={Tick02Icon} size={15} strokeWidth={3} />}
            </span>
            <strong>{partLabels[part]}</strong>
          </button>
        ))}
      </div>

      {'dpp' in item.parts && item.parts.dpp && (
        <label className="accuracy-control">
          <span>
            <strong>DPP accuracy</strong>
            <b>{item.accuracy ?? 70}%</b>
          </span>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={item.accuracy ?? 70}
            onChange={(event) => onAccuracy(Number(event.target.value))}
          />
          <small>Low accuracy creates a review suggestion.</small>
        </label>
      )}

      <div className="task-footer">
        <button className="primary-button" type="button" onClick={onEndDay}>
          End day <HugeiconsIcon icon={ArrowRight01Icon} size={17} />
        </button>
      </div>
    </article>
  );
}

function ScheduleView({
  document,
  today,
  commit,
  autoRebalance = false,
  onAutoRebalanceChange,
}: ViewProps) {
  const [cursor, setCursor] = useState(today.slice(0, 7));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | ScheduleItem['kind']>('all');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const items = orderedItems(document);
  const monthItems = items.filter((item) => item.date.startsWith(cursor));
  const normalizedQuery = query.trim().toLowerCase();
  const filteredItems = monthItems.filter(
    (item) =>
      (filter === 'all' || item.kind === filter) &&
      (!normalizedQuery ||
        item.topic.toLowerCase().includes(normalizedQuery) ||
        item.chapter.toLowerCase().includes(normalizedQuery)),
  );
  const [year, month] = cursor.split('-').map(Number);
  const monthName = new Intl.DateTimeFormat('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year!, month! - 1, 1)));

  function shiftMonth(delta: number) {
    const date = new Date(Date.UTC(year!, month! - 1 + delta, 1));
    setCursor(`${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`);
  }

  return (
    <>
      <PageHeader
        eyebrow="Plan"
        title="Schedule"
        action={
          <button className="secondary-button" onClick={() => setSettingsOpen(!settingsOpen)}>
            <HugeiconsIcon icon={Settings02Icon} size={16} /> Rules
          </button>
        }
      />
      {settingsOpen && (
        <section className="card settings-panel">
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={autoRebalance}
              onChange={(event) => onAutoRebalanceChange?.(event.target.checked)}
            />
            <span>
              <b>Auto-rebalance</b>
              <small>Spread missed work across the remaining month.</small>
            </span>
          </label>
          <div>
            <label htmlFor="weak-threshold">Weak-topic threshold</label>
            <p>Accuracy below this creates an editable revision suggestion.</p>
          </div>
          <div className="threshold-input">
            <input
              id="weak-threshold"
              type="range"
              min="30"
              max="90"
              step="5"
              value={document.weakThreshold}
              onChange={(event) =>
                commit({ ...document, weakThreshold: Number(event.target.value) })
              }
            />
            <b>{document.weakThreshold}%</b>
          </div>
        </section>
      )}
      <section className="card schedule-card">
        <div className="month-header">
          <button
            className="icon-button"
            onClick={() => shiftMonth(-1)}
            aria-label="Previous month"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} />
          </button>
          <h2>{monthName}</h2>
          <button className="icon-button" onClick={() => shiftMonth(1)} aria-label="Next month">
            <HugeiconsIcon icon={ArrowRight01Icon} />
          </button>
        </div>
        <CalendarGrid
          key={cursor}
          year={year!}
          month={month!}
          today={today}
          items={monthItems}
          unavailableDays={document.unavailableDays ?? {}}
          onSelect={setSelectedDate}
        />
      </section>
      <div className="schedule-toolbar">
        <label className="search-field" htmlFor="schedule-search">
          <HugeiconsIcon icon={Search01Icon} size={15} />
          <input
            id="schedule-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search topic or chapter…"
          />
          <kbd>/</kbd>
        </label>
        <label className="filter-field">
          <HugeiconsIcon icon={FilterHorizontalIcon} size={14} />
          <span className="sr-only">Filter schedule</span>
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value as typeof filter)}
          >
            <option value="all">All sessions</option>
            <option value="study">Study</option>
            <option value="buffer">Buffers</option>
            <option value="revision">Revision</option>
            <option value="recall">Recall</option>
            <option value="carryover">Carry-over</option>
          </select>
        </label>
      </div>
      <section className="agenda-list">
        {filteredItems.map((item) => (
          <AgendaItem item={item} key={item.id} document={document} commit={commit} today={today} />
        ))}
        {filteredItems.length === 0 && (
          <div className="empty-search">
            <HugeiconsIcon icon={Search01Icon} size={18} /> Nothing matches this view. Try another
            month or filter.
          </div>
        )}
      </section>
      {selectedDate && (
        <DayEditor
          date={selectedDate}
          document={document}
          autoRebalance={autoRebalance}
          commit={commit}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </>
  );
}

function CalendarGrid({
  year,
  month,
  today,
  items,
  unavailableDays,
  onSelect,
}: {
  year: number;
  month: number;
  today: string;
  items: ScheduleItem[];
  unavailableDays: TrackerDocument['unavailableDays'];
  onSelect: (date: string) => void;
}) {
  const firstDay = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells = Array.from({ length: firstDay + days }, (_, index) =>
    index < firstDay ? null : index - firstDay + 1,
  );
  return (
    <div className="calendar">
      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
        <span className="weekday" key={`${day}-${index}`}>
          {day}
        </span>
      ))}
      {cells.map((day, index) => {
        if (!day) return <span key={`empty-${index}`} />;
        const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const dayItems = items.filter((item) => item.date === date && item.status !== 'skipped');
        const unavailable = unavailableDays?.[date];
        const state = dayItems.some((item) => item.status === 'complete')
          ? 'complete'
          : dayItems.some((item) => item.status === 'partial')
            ? 'partial'
            : '';
        return (
          <button
            type="button"
            title={dayItems.map((item) => item.topic).join(', ')}
            className={`calendar-day ${date === today ? 'today' : ''} ${state} ${unavailable ? 'unavailable' : ''}`}
            key={date}
            onClick={() => onSelect(date)}
            aria-label={`${formatLongDate(date)}${unavailable ? `, unavailable: ${unavailable.reason}` : ''}${dayItems.length ? `, ${dayItems.length} sessions` : ''}`}
          >
            {day}
            <i>
              {dayItems.slice(0, 3).map((item) => (
                <b className={`dot dot-${item.kind}`} key={item.id} />
              ))}
            </i>
          </button>
        );
      })}
    </div>
  );
}

function DayEditor({
  date,
  document,
  autoRebalance,
  commit,
  onClose,
}: {
  date: string;
  document: TrackerDocument;
  autoRebalance: boolean;
  commit: (document: TrackerDocument) => void;
  onClose: () => void;
}) {
  const unavailable = document.unavailableDays?.[date];
  const [mode, setMode] = useState<'session' | 'unavailable'>(
    unavailable ? 'unavailable' : 'session',
  );
  const [topic, setTopic] = useState('');
  const [chapter, setChapter] = useState(document.title);
  const [minutes, setMinutes] = useState(45);
  const [kind, setKind] = useState<'study' | 'test' | 'revision' | 'recall' | 'practice'>('study');
  const [reason, setReason] = useState<UnavailableReason>(unavailable?.reason ?? 'test');
  const [note, setNote] = useState(unavailable?.note ?? '');
  const dayItems = orderedItems(document).filter((item) => item.date === date);
  const preview = previewUnavailableDay(document, date, autoRebalance);

  function addSession() {
    if (!topic.trim()) return;
    commit(addManualSession(document, { date, topic, chapter, minutes, kind }));
    onClose();
  }

  function blockDay() {
    commit(markUnavailableDay(document, { date, reason, note, autoRebalance }));
    onClose();
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="modal-card day-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="day-editor-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="day-editor-header">
          <div>
            <span className="eyebrow">Calendar</span>
            <h2 id="day-editor-title">{formatLongDate(date)}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Close date editor"
          >
            <HugeiconsIcon icon={Cancel01Icon} size={17} />
          </button>
        </header>

        {dayItems.length > 0 && (
          <div className="day-event-list">
            {dayItems.map((item) => (
              <span key={item.id}>
                <i className={`dot dot-${item.kind}`} />
                <b>{item.topic}</b>
                <small>{item.durationMinutes} min</small>
              </span>
            ))}
          </div>
        )}

        <div className="day-editor-tabs" role="tablist">
          <button
            type="button"
            className={mode === 'session' ? 'active' : ''}
            onClick={() => setMode('session')}
          >
            Add session
          </button>
          <button
            type="button"
            className={mode === 'unavailable' ? 'active' : ''}
            onClick={() => setMode('unavailable')}
          >
            Mark unavailable
          </button>
        </div>

        {mode === 'session' ? (
          <div className="day-editor-form">
            <label className="wide-field">
              Topic
              <input
                autoFocus
                value={topic}
                maxLength={320}
                onChange={(event) => setTopic(event.target.value)}
                placeholder="What will you study?"
              />
            </label>
            <label>
              Chapter
              <input
                value={chapter}
                maxLength={180}
                onChange={(event) => setChapter(event.target.value)}
              />
            </label>
            <label>
              Type
              <select value={kind} onChange={(event) => setKind(event.target.value as typeof kind)}>
                <option value="study">Study</option>
                <option value="practice">Practice</option>
                <option value="revision">Revision</option>
                <option value="recall">Recall</option>
                <option value="test">Test</option>
              </select>
            </label>
            <label>
              Minutes
              <input
                type="number"
                min="5"
                max="600"
                step="5"
                value={minutes}
                onChange={(event) => setMinutes(Number(event.target.value))}
              />
            </label>
            <div className="dialog-actions wide-field">
              <button className="text-button" type="button" onClick={onClose}>
                Cancel
              </button>
              <button
                className="primary-button"
                type="button"
                disabled={!topic.trim()}
                onClick={addSession}
              >
                Add to calendar
              </button>
            </div>
          </div>
        ) : unavailable ? (
          <div className="unavailable-summary">
            <span className="status-pill danger">Unavailable</span>
            <h3>{unavailable.reason}</h3>
            {unavailable.note && <p>{unavailable.note}</p>}
            <small>
              Restoring the date does not pull redistributed sessions back automatically.
            </small>
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                commit(restoreAvailableDay(document, date));
                onClose();
              }}
            >
              Make date available
            </button>
          </div>
        ) : (
          <div className="day-editor-form">
            <label>
              Reason
              <select
                value={reason}
                onChange={(event) => setReason(event.target.value as UnavailableReason)}
              >
                <option value="test">Test or exam</option>
                <option value="school">School commitment</option>
                <option value="rest">Rest</option>
                <option value="travel">Travel</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="wide-field">
              Note
              <input
                value={note}
                maxLength={240}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Optional context"
              />
            </label>
            <div className="reschedule-preview wide-field">
              <b>
                {preview.affectedSessions
                  ? `${preview.affectedSessions} session${preview.affectedSessions === 1 ? '' : 's'} will move`
                  : 'No sessions need moving'}
              </b>
              <p>
                {autoRebalance
                  ? 'Work will be spread across the lightest remaining days this month.'
                  : 'Work will move to the next available date.'}
              </p>
              {preview.newFinishDate !== preview.oldFinishDate && (
                <small>Projected finish: {formatShortDate(preview.newFinishDate)}</small>
              )}
            </div>
            <div className="dialog-actions wide-field">
              <button className="text-button" type="button" onClick={onClose}>
                Cancel
              </button>
              <button className="primary-button" type="button" onClick={blockDay}>
                Confirm unavailable
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function AgendaItem({
  item,
  document,
  commit,
  today,
}: {
  item: ScheduleItem;
  document: TrackerDocument;
  commit: (document: TrackerDocument) => void;
  today?: string;
}) {
  const pending = item.suggestionStatus === 'pending';
  return (
    <article className={`agenda-item ${item.status === 'complete' ? 'done' : ''}`}>
      <time>{formatShortDate(item.date)}</time>
      <span className={`agenda-icon kind-${item.kind}`}>
        {item.kind === 'recall' ? (
          <HugeiconsIcon icon={RotateCcwIcon} />
        ) : item.kind === 'buffer' ? (
          <HugeiconsIcon icon={TimerResetIcon} />
        ) : item.kind === 'revision' ? (
          <HugeiconsIcon icon={Brain01Icon} />
        ) : (
          <HugeiconsIcon icon={BookOpen01Icon} />
        )}
      </span>
      <div>
        <strong>{item.topic}</strong>
        <p>
          {item.chapter} · {item.durationMinutes} min
        </p>
      </div>
      {pending ? (
        <div className="suggestion-actions">
          <label className="sr-only" htmlFor={`date-${item.id}`}>
            Suggested date
          </label>
          <input
            id={`date-${item.id}`}
            type="date"
            value={item.date}
            onChange={(event) =>
              commit(updateSuggestion(document, item.id, 'accepted', event.target.value))
            }
          />
          <button
            className="small-button"
            onClick={() => commit(updateSuggestion(document, item.id, 'accepted'))}
          >
            Add
          </button>
          <button
            className="icon-button"
            aria-label="Dismiss suggestion"
            onClick={() => commit(updateSuggestion(document, item.id, 'dismissed'))}
          >
            <HugeiconsIcon icon={Cancel01Icon} size={16} />
          </button>
        </div>
      ) : today &&
        item.date > today &&
        item.status === 'scheduled' &&
        item.origin !== 'automation' ? (
        <button
          className="small-button"
          onClick={() => commit(moveItemAhead(document, item.id, today))}
        >
          Do today
        </button>
      ) : (
        <span className={`status-label status-${item.status}`}>
          {item.bufferState === 'consumed' ? 'absorbed delay' : item.status}
        </span>
      )}
    </article>
  );
}

function ProgressView({ document, today }: Omit<ViewProps, 'commit'>) {
  const completed = document.items.filter((item) => item.status === 'complete').length;
  const active = document.items.filter(
    (item) => item.status !== 'skipped' && item.suggestionStatus !== 'dismissed',
  ).length;
  const streak = currentStreak(document, today);
  const finish = projectedFinishDate(document, today);
  const weakTopics = document.items.filter(
    (item) => item.accuracy !== null && item.accuracy < document.weakThreshold,
  );
  return (
    <>
      <PageHeader
        eyebrow="Progress"
        title="Your rhythm"
        action={
          <button className="secondary-button" onClick={() => downloadTracker(document)}>
            <HugeiconsIcon icon={Download01Icon} size={15} /> Export data
          </button>
        }
      />
      <section className="metric-grid">
        <Metric
          icon={<HugeiconsIcon icon={FireIcon} />}
          label="Current rhythm"
          value={`${streak} day${streak === 1 ? '' : 's'}`}
          tone="accent"
        />
        <Metric
          icon={<HugeiconsIcon icon={DashboardSpeed01Icon} />}
          label="Plan progress"
          value={`${Math.round((completed / active) * 100) || 0}%`}
          detail={`${completed} of ${active} sessions`}
        />
        <Metric
          icon={<HugeiconsIcon icon={PartyPopperIcon} />}
          label="At this pace"
          value={formatShortDate(finish)}
          detail="projected finish"
          tone="sage"
        />
      </section>
      <section className="card heatmap-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Last 70 days</span>
            <h2>Your consistency</h2>
          </div>
          <span className="heatmap-legend">
            quiet <i />
            <i />
            <i /> focused
          </span>
        </div>
        <Heatmap document={document} today={today} />
      </section>
      <section className="card weak-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Accuracy signals</span>
            <h2>Topics asking for another pass</h2>
          </div>
          <HugeiconsIcon icon={Brain01Icon} />
        </div>
        {weakTopics.length ? (
          weakTopics.map((item) => (
            <div className="weak-row" key={item.id}>
              <span>
                <strong>{item.topic}</strong>
                <small>{item.chapter}</small>
              </span>
              <b>{item.accuracy}%</b>
            </div>
          ))
        ) : (
          <p className="muted-copy">
            No weak topics logged yet. Accuracy, not checkbox count, will populate this view.
          </p>
        )}
      </section>
    </>
  );
}

function Heatmap({ document, today }: { document: TrackerDocument; today: string }) {
  return (
    <div className="heatmap">
      {Array.from({ length: 70 }, (_, index) => addDays(today, index - 69)).map((date) => {
        const activity = document.activity[date];
        const ratio =
          activity && activity.totalParts ? activity.completedParts / activity.totalParts : 0;
        const level = ratio === 0 ? 0 : ratio < 0.5 ? 1 : ratio < 1 ? 2 : 3;
        return (
          <span
            key={date}
            className={`heat level-${level}`}
            title={`${formatShortDate(date)}: ${activity?.completedParts ?? 0} parts`}
          />
        );
      })}
    </div>
  );
}

function MistakesView({ document, commit }: Omit<ViewProps, 'today'>) {
  const [chapter, setChapter] = useState(document.items[0]?.chapter ?? 'General');
  const [kind, setKind] = useState<Mistake['kind']>('careless');
  const [note, setNote] = useState('');
  const chapters = [...new Set(document.items.map((item) => item.chapter))];

  function addMistake() {
    if (!note.trim()) return;
    const mistake: Mistake = {
      id: crypto.randomUUID(),
      chapter,
      topic: '',
      kind,
      note: note.trim(),
      createdAt: new Date().toISOString(),
    };
    commit({ ...document, mistakes: [mistake, ...document.mistakes] });
    setNote('');
  }

  return (
    <>
      <PageHeader eyebrow="Revision cues" title="Mistakes" />
      <section className="card mistake-form">
        <div className="form-grid">
          <label>
            Chapter
            <select value={chapter} onChange={(event) => setChapter(event.target.value)}>
              {chapters.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <fieldset>
            <legend>What kind?</legend>
            <div className="segmented">
              <button
                className={kind === 'careless' ? 'active' : ''}
                type="button"
                onClick={() => setKind('careless')}
              >
                Careless
              </button>
              <button
                className={kind === 'conceptual' ? 'active' : ''}
                type="button"
                onClick={() => setKind('conceptual')}
              >
                Conceptual
              </button>
            </div>
          </fieldset>
        </div>
        <label>
          What went wrong?
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') addMistake();
            }}
            placeholder="One useful sentence is enough…"
            maxLength={1000}
          />
        </label>
        <button
          className="primary-button"
          type="button"
          disabled={!note.trim()}
          onClick={addMistake}
        >
          Add to log <HugeiconsIcon icon={ArrowRight01Icon} size={17} />
        </button>
      </section>
      <section className="mistake-list">
        {document.mistakes.length ? (
          document.mistakes.map((mistake) => (
            <article className="card mistake-entry" key={mistake.id}>
              <span className={`mistake-kind ${mistake.kind}`}>
                {mistake.kind === 'careless' ? (
                  <HugeiconsIcon icon={AlertCircleIcon} />
                ) : (
                  <HugeiconsIcon icon={Brain01Icon} />
                )}
                {mistake.kind}
              </span>
              <div>
                <strong>{mistake.chapter}</strong>
                <p>{mistake.note}</p>
              </div>
              <time>{formatShortDate(mistake.createdAt.slice(0, 10))}</time>
            </article>
          ))
        ) : (
          <div className="card empty-inline">
            <HugeiconsIcon icon={SparklesIcon} />
            <p>Your mistake log is empty. That’s okay — add only notes you’ll actually revisit.</p>
          </div>
        )}
      </section>
    </>
  );
}

function SuggestionList({
  document,
  items,
  commit,
}: {
  document: TrackerDocument;
  items: ScheduleItem[];
  commit: (document: TrackerDocument) => void;
}) {
  return (
    <section className="suggestion-box">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Tarpy suggests</span>
          <h2>Small revision touchpoints</h2>
        </div>
        <HugeiconsIcon icon={SparklesIcon} />
      </div>
      {items.slice(0, 6).map((item) => (
        <AgendaItem item={item} document={document} commit={commit} key={item.id} />
      ))}
    </section>
  );
}

function RescheduleDialog({
  proposal,
  onClose,
  onApply,
}: {
  proposal: RescheduleProposal;
  onClose: () => void;
  onApply: (proposal: RescheduleProposal) => void;
}) {
  const [date, setDate] = useState(proposal.carryoverDate ?? '');
  const dateChanged = Boolean(date && proposal.carryoverDate && date !== proposal.carryoverDate);
  const accepted = dateChanged
    ? {
        ...proposal,
        mode: 'manual-date' as const,
        carryoverDate: date,
        bufferItemId: undefined,
        newFinishDate: date > proposal.oldFinishDate ? date : proposal.oldFinishDate,
      }
    : proposal;
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="reschedule-title">
        <button className="modal-close icon-button" onClick={onClose} aria-label="Close">
          <HugeiconsIcon icon={Cancel01Icon} />
        </button>
        <span className="modal-symbol">
          {proposal.mode === 'no-change' ? (
            <HugeiconsIcon icon={PartyPopperIcon} />
          ) : proposal.mode === 'use-buffer' ? (
            <HugeiconsIcon icon={TimerResetIcon} />
          ) : (
            <HugeiconsIcon icon={Calendar03Icon} />
          )}
        </span>
        <span className="eyebrow">Before anything moves</span>
        <h2 id="reschedule-title">
          {proposal.mode === 'no-change'
            ? 'Day complete.'
            : proposal.mode === 'use-buffer'
              ? 'Your buffer has this.'
              : 'Shift by one day?'}
        </h2>
        <p>{proposal.message}</p>
        {proposal.mode !== 'no-change' && (
          <label className="override-date">
            Carry remaining work on
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
            <small>
              {dateChanged
                ? 'Manual override: no buffer or automatic shift will be applied.'
                : 'Change this date if you know better than the plan.'}
            </small>
          </label>
        )}
        <div className="date-comparison">
          <span>
            <small>Current finish</small>
            <strong>{formatShortDate(proposal.oldFinishDate)}</strong>
          </span>
          <HugeiconsIcon icon={ArrowRight01Icon} />
          <span>
            <small>New finish</small>
            <strong>{formatShortDate(accepted.newFinishDate)}</strong>
          </span>
        </div>
        <div className="modal-actions">
          <button className="secondary-button" onClick={onClose}>
            Keep today open
          </button>
          <button className="primary-button" onClick={() => onApply(accepted)}>
            {proposal.mode === 'no-change'
              ? 'Finish day'
              : dateChanged
                ? 'Use my date'
                : 'Accept suggestion'}{' '}
            <HugeiconsIcon icon={Tick02Icon} size={17} />
          </button>
        </div>
      </section>
    </div>
  );
}

function Landing() {
  const error = new URLSearchParams(window.location.search).get('auth_error');
  return (
    <main className="landing">
      <MeshBackdrop />
      <nav className="landing-nav">
        <a className="brand-lockup" href="/">
          <img src="/brand/icon.svg" alt="" className="brand-mark" />
          <span>
            tarpy<span>.party</span>
          </span>
        </a>
        <span className="landing-nav-links">
          <a href="/leaderboard">Leaderboard</a>
          <span className="landing-tag">Adaptive study planner</span>
        </span>
      </nav>
      <section className="hero">
        <div className="hero-copy">
          <h1>
            Study what
            <br />
            <em>matters next.</em>
          </h1>
          <p>Progress, accuracy and recall in one calm plan.</p>
          {error && <div className="auth-error">Sign-in didn’t finish. Please try again.</div>}
          <div className="hero-actions">
            <a className="google-button" href="/api/auth/google/start">
              <GoogleGlyph /> Continue with Google
            </a>
          </div>
        </div>
        <HeroPreview />
      </section>
      <section className="principles" aria-label="Key features">
        <article>
          <HugeiconsIcon icon={Tick02Icon} />
          <h3>Partial progress</h3>
        </article>
        <article>
          <HugeiconsIcon icon={Brain01Icon} />
          <h3>Accuracy-led review</h3>
        </article>
        <article>
          <HugeiconsIcon icon={TimerResetIcon} />
          <h3>Adaptive scheduling</h3>
        </article>
      </section>
      <footer>
        <span>tarpy.party</span>
        <nav>
          <a href="/leaderboard">Leaderboard</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
        </nav>
      </footer>
    </main>
  );
}

function AdminPortal({
  user,
  signingOut,
  onSignOut,
}: {
  user: SessionUser;
  signingOut: boolean;
  onSignOut: () => Promise<void>;
}) {
  return (
    <div className="app-shell admin-shell">
      <MeshBackdrop quiet />
      <header className="topbar">
        <a className="brand-lockup" href="/admin" aria-label="tarpy.party admin home">
          <img src="/brand/icon.svg" alt="" className="brand-mark" />
          <span>
            tarpy<span>.party</span>
          </span>
        </a>
        <div className="topbar-actions">
          <span className="admin-identity">{user.email}</span>
          <a className="secondary-button compact-button" href="/">
            Study app
          </a>
          <button
            className="logout-button"
            type="button"
            disabled={signingOut}
            onClick={() => void onSignOut()}
          >
            <HugeiconsIcon icon={Logout01Icon} size={17} />
            <span>{signingOut ? 'Logging out…' : 'Log out'}</span>
          </button>
        </div>
      </header>
      <main className="admin-portal-content">
        <AdminView />
      </main>
    </div>
  );
}

function AdminAccessDenied() {
  return (
    <main className="error-screen">
      <HugeiconsIcon icon={ShieldUserIcon} size={32} />
      <h1>Admin access required</h1>
      <p>This Google account is not on the production admin allowlist.</p>
      <a className="secondary-button" href="/">
        Back to tarpy
      </a>
    </main>
  );
}

function PublicLeaderboardPage({ signedIn }: { signedIn: boolean }) {
  return (
    <main className="public-leaderboard-page">
      <MeshBackdrop quiet />
      <nav className="landing-nav">
        <a className="brand-lockup" href="/">
          <img src="/brand/icon.svg" alt="" className="brand-mark" />
          <span>
            tarpy<span>.party</span>
          </span>
        </a>
        <a
          className="secondary-button compact-button"
          href={signedIn ? '/' : '/api/auth/google/start'}
        >
          {signedIn ? 'Open dashboard' : 'Join with Google'}
        </a>
      </nav>
      <section className="public-leaderboard-content">
        <header>
          <span className="eyebrow">Public standings</span>
          <h1>Consistency, made visible.</h1>
          <p>Points reward completed study parts, real sessions, streaks, and DPP accuracy.</p>
        </header>
        <LeaderboardView canReport={false} publicPage />
      </section>
    </main>
  );
}

function LegalPage({ page }: { page: 'privacy' | 'terms' }) {
  const privacy = page === 'privacy';
  return (
    <main className="legal-page">
      <MeshBackdrop quiet />
      <nav className="landing-nav">
        <a className="brand-lockup" href="/">
          <img src="/brand/icon.svg" alt="" className="brand-mark" />
          <span>
            tarpy<span>.party</span>
          </span>
        </a>
        <a className="text-link" href="/">
          Back home
        </a>
      </nav>
      <article>
        <span className="eyebrow">Last updated 30 August 2026</span>
        <h1>{privacy ? 'Privacy, in plain language.' : 'Terms of use.'}</h1>
        {privacy ? (
          <>
            <p>
              Tarpy stores the Google profile details needed to identify your account: your Google
              account ID, email address, display name and optional profile image. It also stores the
              study plans, completion data, accuracy scores and mistake notes you choose to add.
            </p>
            <h2>How the data is used</h2>
            <p>
              Your data is used only to sign you in, save your tracker and calculate your schedule
              and progress views. Tarpy does not sell personal data or use study notes for
              advertising.
            </p>
            <h2>Infrastructure and cookies</h2>
            <p>
              Tarpy runs on Cloudflare Workers and D1. A secure, HTTP-only session cookie keeps you
              signed in. Google handles the consent and authentication screen; Tarpy never receives
              your Google password.
            </p>
            <h2>Your choices</h2>
            <p>
              You can download your complete account record or permanently delete your account from
              Settings. Contact privacy@tarpy.party if you cannot access those controls.
            </p>
          </>
        ) : (
          <>
            <p>
              Tarpy is an adaptive study-planning tool. It offers suggestions, not guarantees about
              grades, exam outcomes or completion dates.
            </p>
            <h2>Your account</h2>
            <p>
              Use your own Google account and keep access to it secure. Do not use Tarpy to store
              sensitive personal, medical or financial information.
            </p>
            <h2>Schedule suggestions</h2>
            <p>
              Revision, recall and rescheduling suggestions are optional. You remain responsible for
              reviewing and changing the plan to fit your real commitments.
            </p>
            <h2>Availability</h2>
            <p>
              This is an early product and may change. Reasonable care is taken to protect saved
              work, but you should not treat Tarpy as the only copy of information you cannot
              replace.
            </p>
          </>
        )}
      </article>
    </main>
  );
}

function HeroPreview() {
  return (
    <div className="hero-preview">
      <div className="preview-glow" />
      <div className="preview-top">
        <span>
          <img src="/brand/icon.svg" alt="" />
          Today
        </span>
        <b>2/3</b>
      </div>
      <span className="kind-badge kind-study">Study session</span>
      <h2>Alkenes — addition rules</h2>
      <p>Hydrocarbons · about 90 min</p>
      <div className="fake-parts">
        <span className="done">
          <i>
            <HugeiconsIcon icon={Tick02Icon} />
          </i>
          Lecture watched
        </span>
        <span className="done">
          <i>
            <HugeiconsIcon icon={Tick02Icon} />
          </i>
          Notes made
        </span>
        <span>
          <i />
          DPP done
        </span>
      </div>
      <div className="preview-note">
        <HugeiconsIcon icon={TimerResetIcon} />
        <span>
          <small>Buffer protected</small>Your finish date stays 10 Oct
        </span>
      </div>
    </div>
  );
}

function PaceCard({ document, today }: { document: TrackerDocument; today: string }) {
  return (
    <div className="pace-card">
      <div>
        <span>
          <HugeiconsIcon icon={PartyPopperIcon} size={16} /> At your pace
        </span>
        <strong>{formatShortDate(projectedFinishDate(document, today))}</strong>
        <small>projected finish</small>
      </div>
      <div className="pace-line">
        <i />
      </div>
    </div>
  );
}

function NavButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className={`nav-button ${active ? 'active' : ''}`} onClick={onClick}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

function MoreView({
  isAdmin,
  onSelect,
  onSignOut,
  signingOut,
}: {
  isAdmin: boolean;
  onSelect: (tab: Tab) => void;
  onSignOut: () => Promise<void>;
  signingOut: boolean;
}) {
  const links: Array<{
    tab: Tab;
    label: string;
    detail: string;
    icon: React.ReactNode;
  }> = [
    {
      tab: 'mistakes',
      label: 'Mistakes',
      detail: 'Careless and conceptual notes',
      icon: <HugeiconsIcon icon={NotebookPenIcon} />,
    },
    {
      tab: 'leaderboard',
      label: 'Leaderboard',
      detail: 'Public consistency ranking',
      icon: <HugeiconsIcon icon={RankingIcon} />,
    },
    {
      tab: 'settings',
      label: 'Settings',
      detail: 'Profile, privacy, and calendar rules',
      icon: <HugeiconsIcon icon={Settings02Icon} />,
    },
  ];
  if (isAdmin)
    links.push({
      tab: 'admin',
      label: 'Admin',
      detail: 'Users, reports, and audit log',
      icon: <HugeiconsIcon icon={ShieldUserIcon} />,
    });
  return (
    <div className="more-view">
      <PageHeader eyebrow="Workspace" title="More" />
      <div className="more-grid">
        {links.map((link) => (
          <button type="button" key={link.tab} onClick={() => onSelect(link.tab)}>
            <span>{link.icon}</span>
            <div>
              <b>{link.label}</b>
              <small>{link.detail}</small>
            </div>
            <HugeiconsIcon icon={ArrowRight01Icon} />
          </button>
        ))}
        <button
          type="button"
          aria-label="Log out"
          disabled={signingOut}
          onClick={() => void onSignOut()}
        >
          <span>
            <HugeiconsIcon icon={Logout01Icon} />
          </span>
          <div>
            <b>{signingOut ? 'Logging out…' : 'Log out'}</b>
            <small>End this session on this device</small>
          </div>
          <HugeiconsIcon icon={ArrowRight01Icon} />
        </button>
      </div>
    </div>
  );
}

function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </header>
  );
}

function Metric({
  icon,
  label,
  value,
  detail,
  tone = '',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail?: string;
  tone?: string;
}) {
  return (
    <article className={`card metric ${tone}`}>
      <span>{icon}</span>
      <small>{label}</small>
      <strong>{value}</strong>
      {detail && <p>{detail}</p>}
    </article>
  );
}

function SaveIndicator({ status, error }: { status: SaveStatus; error: string | null }) {
  return (
    <span className={`save-indicator ${status}`} title={error ?? undefined}>
      {status === 'saving' ? 'Saving…' : status === 'error' ? 'Not saved' : 'Saved'}
    </span>
  );
}

function EmptyToday({ next }: { next?: ScheduleItem }) {
  return (
    <section className="card empty-today">
      <span>
        <HugeiconsIcon icon={PartyPopperIcon} />
      </span>
      <h2>Your plan is clear today.</h2>
      <p>
        {next
          ? `Next up: ${next.topic} on ${formatShortDate(next.date)}.`
          : 'You’ve reached the end of this plan.'}
      </p>
    </section>
  );
}

function BootScreen() {
  return (
    <div className="boot-screen">
      <MeshBackdrop quiet />
      <img src="/brand/icon.svg" alt="" />
      <span />
      <span />
      <span />
    </div>
  );
}

function ErrorScreen() {
  return (
    <div className="error-screen">
      <HugeiconsIcon icon={AlertCircleIcon} />
      <h1>Tarpy couldn’t open.</h1>
      <p>Check your connection and try again.</p>
      <button className="primary-button" onClick={() => window.location.reload()}>
        Try again
      </button>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M21.6 12.2c0-.7-.1-1.5-.2-2.2H12v4.3h5.4a4.6 4.6 0 0 1-2 3v2.8h3.6c2.1-2 3.3-4.8 3.3-7.9Z"
      />
      <path
        fill="#34A853"
        d="M12 22c3 0 5.5-1 7.3-2.7l-3.6-2.8a6.6 6.6 0 0 1-9.8-3.4H2.2V16A11 11 0 0 0 12 22Z"
      />
      <path fill="#FBBC05" d="M5.9 13.1a6.6 6.6 0 0 1 0-4.2V6H2.2a11 11 0 0 0 0 10l3.7-2.9Z" />
      <path
        fill="#EA4335"
        d="M12 5.4c1.7 0 3.2.6 4.4 1.7l3.2-3.2A10.7 10.7 0 0 0 2.2 6l3.7 2.9A6.5 6.5 0 0 1 12 5.4Z"
      />
    </svg>
  );
}

function kindLabel(kind: ScheduleItem['kind']): string {
  return {
    study: 'Study session',
    test: 'Self-test',
    buffer: 'Buffer + test',
    revision: 'Weak-topic revision',
    recall: 'Spaced recall',
    carryover: 'Carried gently',
    practice: 'Extra practice',
  }[kind];
}

function currentStreak(document: TrackerDocument, today: string): number {
  let streak = 0;
  let cursor = today;
  while (document.activity[cursor]?.completedParts) {
    streak += 1;
    cursor = addDays(cursor, -1);
  }
  return streak;
}

function downloadTracker(document: TrackerDocument): void {
  const blob = new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement('a');
  anchor.href = url;
  anchor.download = `tarpy-${document.startDate}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
